import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, History, Info, KeyRound, Loader2, Plus, ShieldAlert, Trash2, X } from 'lucide-react';
import type { UserProfile } from '../../types';
import type { RechtInfo } from '../../services/rechte/katalog';
import {
    ladeProtokoll,
    ladeRollenStand,
    legeRolleAn,
    loescheRolle,
    setzeRecht,
    type ProtokollZeile,
    type RolleZeile,
    type RollenStand,
} from '../../services/rechte/rollenVerwaltung';
import { aktualisiereMeineRechte } from '../../services/rechte/rechteService';
import { Button } from '../Button';
import {
    fehlerText,
    firmenAuswahl,
    gruppiereRechte,
    hatRolleRecht,
    loeschSperre,
    protokollTeile,
    pruefeRollenName,
    BESCHREIBUNG_MAX,
    ROLLENNAME_MAX,
    standMitRecht,
    standNichtVerfuegbar,
    traegerAnzahl,
    wirksameFirma,
    zellSchluessel,
} from './rollenRechteLogic';

/**
 * Admin-Reiter „Rollen & Rechte" (docs/rollensystem-plan.md §6.1): die
 * Rechte-Matrix einer Firma — Zeilen = Rechte nach Gruppen, Spalten = Rollen —,
 * eigene Rollen anlegen und löschen, Änderungsprotokoll.
 *
 * Ein Admin pflegt nur seine Firma, ein Super-Admin wählt sie. Fehlen die
 * Migrationen Nr. 149/150, steht der Grund da und sonst nichts: kein Knopf,
 * der ins Leere läuft. Geschrieben wird nur über `rollenVerwaltung` (RPCs mit
 * Admin- und Firmenprüfung und Protokoll).
 */

interface Props {
    currentUser: UserProfile | null;
    users: UserProfile[];
}

interface Meldung {
    type: 'success' | 'error';
    message: string;
}

interface ProtokollStand {
    firma: string;
    zeilen: ProtokollZeile[] | null;
    fehler: string | null;
}

const MATRIX_GRUPPEN = gruppiereRechte();
const PROTOKOLL_LIMIT = 100;

/** Eigene Rechte dieser Sitzung nachziehen — ein Fehler dort ist kein Fehler der Änderung. */
const eigeneRechteNachziehen = () => {
    aktualisiereMeineRechte().catch(e => console.warn('Eigene Rechte nicht nachgeladen:', e));
};

export const RollenRechteView: React.FC<Props> = ({ currentUser, users }) => {
    const istSuper = currentUser?.role === 'super_admin';
    const firmen = useMemo(() => (istSuper ? firmenAuswahl(users, currentUser) : []), [istSuper, users, currentUser]);
    const [gewaehlteFirma, setGewaehlteFirma] = useState<string | null>(null);
    const firma = wirksameFirma(currentUser, firmen, gewaehlteFirma);

    /**
     * Stand samt der Firma, für die er geladen wurde: nach einem Firmenwechsel
     * zählt der alte nicht mehr, bis der neue da ist.
     */
    const [geladen, setGeladen] = useState<{ firma: string; stand: RollenStand } | null>(null);
    const stand = geladen && geladen.firma === firma ? geladen.stand : null;
    // Nur die jüngste Ladeantwort zählt — und keine, die vor einem Schreiben losging.
    const ladeNr = useRef(0);

    const ladeStand = useCallback(async (companyId: string) => {
        const nr = ++ladeNr.current;
        let s: RollenStand;
        try {
            s = await ladeRollenStand(companyId);
        } catch (e) {
            s = standNichtVerfuegbar(companyId, fehlerText(e));
        }
        if (nr !== ladeNr.current) return;
        setGeladen({ firma: companyId, stand: s });
    }, []);
    useEffect(() => { if (firma) void ladeStand(firma); }, [firma, ladeStand]);

    const [meldung, setMeldung] = useState<Meldung | null>(null);

    // ─── Matrix-Häkchen ──────────────────────────────────────────────────────
    const [zellenLaufen, setZellenLaufen] = useState<ReadonlySet<string>>(() => new Set());
    // Ref neben dem State: sperrt den zweiten Klick, bevor `disabled` gerendert ist.
    const zellenRef = useRef<Set<string>>(new Set());
    const setzeZelleLaeuft = (key: string, laeuft: boolean) => {
        if (laeuft) zellenRef.current.add(key); else zellenRef.current.delete(key);
        setZellenLaufen(new Set(zellenRef.current));
    };

    // ─── Rolle anlegen / löschen ─────────────────────────────────────────────
    const [neuName, setNeuName] = useState('');
    const [neuBeschreibung, setNeuBeschreibung] = useState('');
    const [anlageLaeuft, setAnlageLaeuft] = useState(false);
    const [loeschFrage, setLoeschFrage] = useState<string | null>(null);
    const [loeschLaeuft, setLoeschLaeuft] = useState<string | null>(null);
    // Anlegen und Löschen laden den Stand neu; ein Häkchen dazwischen verwürfe
    // diese Ladung (Ladenummer). Deshalb sperren beide die Matrix, bis sie fertig sind.
    const strukturRef = useRef(false);
    const sperrtMatrix = anlageLaeuft || !!loeschLaeuft;
    const schreibtGerade = sperrtMatrix || zellenLaufen.size > 0;

    // ─── Protokoll ───────────────────────────────────────────────────────────
    const [protokollOffen, setProtokollOffen] = useState(false);
    const [protokoll, setProtokoll] = useState<ProtokollStand | null>(null);
    const protokollNr = useRef(0);

    const ladeProt = useCallback(async (companyId: string) => {
        const nr = ++protokollNr.current;
        let erg: ProtokollStand;
        try {
            const r = await ladeProtokoll(companyId, PROTOKOLL_LIMIT);
            erg = r.ok
                ? { firma: companyId, zeilen: r.zeilen, fehler: null }
                : { firma: companyId, zeilen: null, fehler: r.meldung || 'Protokoll nicht lesbar.' };
        } catch (e) {
            erg = { firma: companyId, zeilen: null, fehler: fehlerText(e) };
        }
        if (nr !== protokollNr.current) return;
        setProtokoll(erg);
    }, []);
    const verfuegbar = !!stand?.verfuegbar;
    useEffect(() => {
        if (protokollOffen && firma && verfuegbar) void ladeProt(firma);
    }, [protokollOffen, firma, verfuegbar, ladeProt]);
    const protokollJetzt = protokoll && protokoll.firma === firma ? protokoll : null;

    const protokollKontext = useMemo(() => ({ users, rollen: stand?.rollen || [] }), [users, stand]);

    /** Nach jeder gespeicherten Änderung: eigene Rechte und ein offenes Protokoll nachziehen. */
    const nachAenderung = (companyId: string) => {
        eigeneRechteNachziehen();
        if (protokollOffen) void ladeProt(companyId);
    };

    /** Häkchen umschalten — optimistisch, bei Fehler zurück (Muster SiFa-Häkchen). */
    const umschalten = async (rolle: RolleZeile, recht: RechtInfo, erlaubt: boolean) => {
        if (!firma || !stand?.verfuegbar || sperrtMatrix) return;
        const key = zellSchluessel(rolle.schluessel, recht.schluessel);
        if (zellenRef.current.has(key) || strukturRef.current) return;
        const schreibFirma = firma;
        const setze = (an: boolean) => setGeladen(prev => (prev && prev.firma === schreibFirma
            ? { firma: schreibFirma, stand: standMitRecht(prev.stand, rolle.schluessel, recht.schluessel, an) }
            : prev));
        setzeZelleLaeuft(key, true);
        setMeldung(null);
        // Eine Ladeantwort von vor dem Schreiben überschriebe sonst den optimistischen Stand.
        ladeNr.current++;
        setze(erlaubt);
        try {
            const r = await setzeRecht(schreibFirma, rolle.schluessel, recht.schluessel, erlaubt);
            if (!r.ok) throw new Error(r.meldung || 'unbekannter Fehler');
            nachAenderung(schreibFirma);
        } catch (e) {
            setze(!erlaubt);
            setMeldung({ type: 'error', message: `${rolle.name} → ${recht.titel} nicht gespeichert: ${fehlerText(e)}` });
        } finally {
            // Auch eine während des Schreibens gestartete Ladung kann den alten Stand gelesen haben.
            ladeNr.current++;
            setzeZelleLaeuft(key, false);
        }
    };

    const nameFehler = neuName.trim() ? pruefeRollenName(neuName, stand?.rollen || []) : null;

    const anlegen = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!firma || !stand?.verfuegbar || strukturRef.current || zellenRef.current.size > 0) return;
        const name = neuName.trim().replace(/\s+/g, ' ');
        const fehler = pruefeRollenName(name, stand.rollen);
        if (fehler) { setMeldung({ type: 'error', message: fehler }); return; }
        const schreibFirma = firma;
        strukturRef.current = true;
        setAnlageLaeuft(true);
        setMeldung(null);
        try {
            const r = await legeRolleAn(schreibFirma, name, neuBeschreibung.trim());
            if (!r.ok) throw new Error(r.meldung || 'unbekannter Fehler');
            setNeuName('');
            setNeuBeschreibung('');
            setMeldung({ type: 'success', message: `Rolle „${name}" angelegt. Sie startet ohne Rechte — in ihrer Spalte die Häkchen setzen.` });
            await ladeStand(schreibFirma);
            nachAenderung(schreibFirma);
        } catch (err) {
            setMeldung({ type: 'error', message: `Rolle nicht angelegt: ${fehlerText(err)}` });
        } finally {
            strukturRef.current = false;
            setAnlageLaeuft(false);
        }
    };

    const loeschen = async (rolle: RolleZeile) => {
        if (!firma || strukturRef.current || zellenRef.current.size > 0) return;
        const schreibFirma = firma;
        strukturRef.current = true;
        setLoeschLaeuft(rolle.schluessel);
        setMeldung(null);
        try {
            const r = await loescheRolle(schreibFirma, rolle.schluessel);
            if (!r.ok) throw new Error(r.meldung || 'unbekannter Fehler');
            setMeldung({ type: 'success', message: `Rolle „${rolle.name}" gelöscht.` });
            await ladeStand(schreibFirma);
            nachAenderung(schreibFirma);
        } catch (e) {
            setMeldung({ type: 'error', message: `Rolle „${rolle.name}" nicht gelöscht: ${fehlerText(e)}` });
        } finally {
            strukturRef.current = false;
            setLoeschLaeuft(null);
            setLoeschFrage(null);
        }
    };

    // ─── Bausteine ───────────────────────────────────────────────────────────

    const rollenKopf = (r: RolleZeile) => {
        const n = traegerAnzahl(stand, r.schluessel);
        const sperre = loeschSperre(stand, r);
        const laeuft = loeschLaeuft === r.schluessel;
        return (
            <div className="flex flex-col items-center gap-1">
                <span className="font-bold text-slate-800 text-xs leading-tight break-words max-w-[8rem]">{r.name}</span>
                <span className="text-[10px] font-normal text-slate-400">{n === 1 ? '1 Person' : `${n} Personen`}</span>
                {r.system ? (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-200 text-slate-600" title="Startrolle — nicht löschbar">Start</span>
                ) : loeschFrage === r.schluessel ? (
                    <div className="flex flex-col items-center gap-1">
                        <span className="text-[10px] font-bold text-red-700">Wirklich löschen?</span>
                        <div className="flex gap-1">
                            <button
                                type="button"
                                onClick={() => { void loeschen(r); }}
                                disabled={!!loeschLaeuft}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-red-600 text-white hover:bg-red-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {laeuft && <Loader2 className="w-3 h-3 animate-spin" />} Ja
                            </button>
                            <button
                                type="button"
                                onClick={() => setLoeschFrage(null)}
                                disabled={!!loeschLaeuft}
                                className="px-2 py-0.5 rounded text-[11px] font-bold bg-white border border-slate-300 text-slate-600 hover:bg-slate-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Nein
                            </button>
                        </div>
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => { setLoeschFrage(r.schluessel); setMeldung(null); }}
                        disabled={!!sperre || schreibtGerade}
                        title={sperre || `Rolle „${r.name}" löschen`}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-slate-400 hover:text-red-600 hover:bg-red-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <Trash2 className="w-3 h-3" /> Löschen
                    </button>
                )}
            </div>
        );
    };

    const zelle = (r: RolleZeile, recht: RechtInfo) => {
        const an = hatRolleRecht(stand, r.schluessel, recht.schluessel);
        const laeuft = zellenLaufen.has(zellSchluessel(r.schluessel, recht.schluessel));
        const aus = laeuft || sperrtMatrix;
        return (
            <label
                className={`relative flex items-center justify-center min-h-[3rem] px-3 ${aus ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                title={`${r.name}: „${recht.titel}" ${an ? 'entziehen' : 'erlauben'}`}
            >
                <input
                    type="checkbox"
                    checked={an}
                    disabled={aus}
                    onChange={() => { void umschalten(r, recht, !an); }}
                    aria-label={`${recht.titel} für ${r.name}`}
                    className="w-4 h-4 accent-indigo-600 cursor-pointer disabled:cursor-not-allowed"
                />
                {laeuft && <Loader2 className="absolute right-1 w-3 h-3 animate-spin text-slate-400" />}
            </label>
        );
    };

    if (!currentUser) {
        return (
            <div className="p-8 flex justify-center text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin" />
            </div>
        );
    }

    const rollen = stand?.rollen || [];

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                            <KeyRound className="w-5 h-5 text-indigo-600" /> Rollen & Rechte
                        </h3>
                        <p className="text-xs text-slate-500 mt-1 max-w-2xl">
                            Welche Rolle was darf. Eine Person kann mehrere Rollen haben, die Rechte addieren sich.
                            Rollen vergeben Sie im Reiter „Benutzer".
                        </p>
                    </div>
                    {istSuper && firmen.length > 0 && (
                        <label className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase">
                            Firma
                            <select
                                value={firma || ''}
                                onChange={e => { setGewaehlteFirma(e.target.value); setLoeschFrage(null); setMeldung(null); }}
                                disabled={schreibtGerade}
                                className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm font-medium normal-case text-slate-700 bg-white cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {firmen.map(f => <option key={f.id} value={f.id}>{f.name} ({f.id})</option>)}
                            </select>
                        </label>
                    )}
                </div>
                <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                    <Info className="w-4 h-4 shrink-0 mt-0.5 text-slate-400" />
                    <div className="space-y-0.5">
                        <p>Admins haben immer alle Rechte und stehen nicht in der Matrix.</p>
                        {/* Stufe 1: Matrix und Rollen werden gepflegt, die App prüft sie noch
                            nicht (docs/rollensystem-plan.md §8). Mit Stufe 2/3 ändert sich der Satz. */}
                        <p>Rollen und Rechte lassen sich schon vergeben; bis zum nächsten Ausbau gelten in der App aber noch die bisherigen Zugriffe.</p>
                    </div>
                </div>
            </div>

            {!firma ? (
                <div className="flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    <ShieldAlert className="w-4 h-4 shrink-0" />
                    Ihr Profil ist keiner Firma zugeordnet (company_id fehlt) — Rollen lassen sich erst danach pflegen.
                </div>
            ) : !stand ? (
                <div className="p-8 flex items-center justify-center gap-2 text-sm text-slate-400">
                    <Loader2 className="w-4 h-4 animate-spin" /> Rollen werden geladen …
                </div>
            ) : !stand.verfuegbar ? (
                <div className="flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    <ShieldAlert className="w-4 h-4 shrink-0" />
                    {stand.grund || 'Rollen & Rechte sind nicht verfügbar.'}
                </div>
            ) : (
                <>
                    {meldung && (
                        <div className={`flex items-start gap-2 rounded-lg p-3 text-sm ${meldung.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                            <span className="flex-1">{meldung.message}</span>
                            <button type="button" onClick={() => setMeldung(null)} className="shrink-0 opacity-60 hover:opacity-100 cursor-pointer" aria-label="Meldung schließen">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    )}

                    {rollen.length === 0 ? (
                        <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
                            Für diese Firma gibt es noch keine Rollen.
                        </div>
                    ) : (
                        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                            <div className="overflow-x-auto">
                                {/* border-separate: mit border-collapse verlieren die
                                    klebenden Zellen beim Scrollen ihre Linien. */}
                                <table className="w-full text-sm border-separate border-spacing-0">
                                    <thead>
                                        <tr>
                                            <th scope="col" className="sticky left-0 z-20 min-w-[16rem] bg-slate-50 border-b border-r border-slate-200 p-3 text-left align-bottom text-xs font-bold uppercase text-slate-500">
                                                Recht
                                            </th>
                                            {rollen.map(r => (
                                                <th key={r.schluessel} scope="col" title={r.beschreibung || undefined} className="min-w-[7rem] bg-slate-50 border-b border-slate-200 p-2 text-center align-bottom">
                                                    {rollenKopf(r)}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {MATRIX_GRUPPEN.map(g => (
                                            <React.Fragment key={g.schluessel}>
                                                <tr>
                                                    <td colSpan={rollen.length + 1} className="bg-slate-100 border-b border-slate-200 px-3 py-1.5">
                                                        <div className="sticky left-3 w-max text-[11px] font-black uppercase tracking-wider text-slate-500">{g.titel}</div>
                                                    </td>
                                                </tr>
                                                {g.rechte.map(recht => (
                                                    <tr key={recht.schluessel} className="group">
                                                        <th scope="row" className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 border-b border-r border-slate-100 p-3 text-left align-top font-normal">
                                                            <div className="font-medium text-slate-800">{recht.titel}</div>
                                                            <div className="mt-0.5 max-w-xs text-xs text-slate-500">{recht.beschreibung}</div>
                                                        </th>
                                                        {rollen.map(r => (
                                                            <td key={r.schluessel} className="border-b border-slate-100 p-0 text-center group-hover:bg-slate-50">
                                                                {zelle(r, recht)}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <form onSubmit={e => { void anlegen(e); }} className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 space-y-3">
                        <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                            <Plus className="w-4 h-4 text-indigo-600" /> Rolle anlegen
                        </h4>
                        <div className="grid gap-3 md:grid-cols-[minmax(0,16rem)_minmax(0,1fr)_auto] md:items-end">
                            <label className="block">
                                <span className="text-xs font-bold text-slate-500 uppercase block mb-1">Name</span>
                                <input
                                    type="text"
                                    value={neuName}
                                    onChange={e => setNeuName(e.target.value)}
                                    maxLength={ROLLENNAME_MAX}
                                    placeholder="z. B. Prüfer"
                                    className="w-full border border-slate-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                />
                            </label>
                            <label className="block">
                                <span className="text-xs font-bold text-slate-500 uppercase block mb-1">Beschreibung (optional)</span>
                                <input
                                    type="text"
                                    value={neuBeschreibung}
                                    onChange={e => setNeuBeschreibung(e.target.value)}
                                    maxLength={BESCHREIBUNG_MAX}
                                    placeholder="Wofür die Rolle steht"
                                    className="w-full border border-slate-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                />
                            </label>
                            <Button
                                type="submit"
                                variant="primary"
                                loading={anlageLaeuft}
                                disabled={!neuName.trim() || !!nameFehler || schreibtGerade}
                                icon={<Plus className="w-4 h-4" />}
                                className="cursor-pointer"
                            >
                                Anlegen
                            </Button>
                        </div>
                        {nameFehler && <p className="text-xs text-red-600">{nameFehler}</p>}
                        <p className="text-xs text-slate-500">Eine neue Rolle startet ohne Rechte — danach in ihrer Spalte die Häkchen setzen.</p>
                    </form>

                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <button
                            type="button"
                            onClick={() => setProtokollOffen(o => !o)}
                            aria-expanded={protokollOffen}
                            className="w-full flex items-center gap-2 p-4 text-left text-sm font-bold text-slate-800 hover:bg-slate-50 cursor-pointer"
                        >
                            {protokollOffen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                            <History className="w-4 h-4 text-slate-500" /> Änderungsprotokoll
                            <span className="text-xs font-normal text-slate-400">die letzten {PROTOKOLL_LIMIT}</span>
                        </button>
                        {protokollOffen && (
                            <div className="border-t border-slate-100">
                                {!protokollJetzt ? (
                                    <div className="p-4 flex items-center gap-2 text-xs text-slate-400">
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Protokoll wird geladen …
                                    </div>
                                ) : protokollJetzt.fehler ? (
                                    <div className="m-4 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">{protokollJetzt.fehler}</div>
                                ) : !protokollJetzt.zeilen || protokollJetzt.zeilen.length === 0 ? (
                                    <p className="p-4 text-xs text-slate-400">Noch keine Änderungen.</p>
                                ) : (
                                    <ul className="max-h-96 overflow-y-auto divide-y divide-slate-100">
                                        {protokollJetzt.zeilen.map(z => {
                                            const t = protokollTeile(z, protokollKontext);
                                            return (
                                                <li key={z.id} className="flex gap-2 px-4 py-2 text-xs text-slate-600">
                                                    <span className="shrink-0 tabular-nums text-slate-400">{t.zeit}</span>
                                                    <span>
                                                        · <span className="font-bold text-slate-700">{t.wer}</span>: {t.was}
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};
